import React, { useState } from 'react';
import { RefreshCw, PlayCircle, CheckCircle2, AlertCircle, Database, Calculator, Lock, Calendar, Save } from 'lucide-react';
import { AccountingAccount, Account } from '../../types';
import { useClosingDate } from '../../hooks/useClosingDate';
import { getSystemSettings, updateSystemSettings } from '../../services/settingsService';

interface AccountingProcessesViewProps {
  userId: string;
  accounts: AccountingAccount[];
  financeAccounts: Account[];
  onInitialize: () => Promise<void>;
  onGenerateInitialBalances: () => Promise<void>;
  onSettleDocuments: () => Promise<void>;
  isInitializing: boolean;
}

export const AccountingProcessesView: React.FC<AccountingProcessesViewProps> = ({ 
  accounts, 
  financeAccounts, 
  onInitialize,
  onGenerateInitialBalances,
  onSettleDocuments,
  isInitializing,
  userId
}) => {
  const [runningProcess, setRunningProcess] = useState<string | null>(null);
  const closingDate = useClosingDate(userId);
  const [isEditingClosingDate, setIsEditingClosingDate] = useState(false);
  const [newClosingDate, setNewClosingDate] = useState('');
  const [isSavingClosingDate, setIsSavingClosingDate] = useState(false);
  
  const isInitialized = accounts.length > 0;

  const handleRunProcess = async (id: string, handler: () => Promise<void>) => {
    setRunningProcess(id);
    try {
      await handler();
    } catch (error: any) {
      console.error(`Error running process ${id}:`, error);
      alert(error.message || `Error al ejecutar el proceso.`);
    } finally {
      setRunningProcess(null);
    }
  };

  const handleSaveClosingDate = async () => {
    if (!newClosingDate) return;
    try {
      setIsSavingClosingDate(true);
      const settings = await getSystemSettings(userId);
      await updateSystemSettings(userId, { ...settings, closingDate: newClosingDate });
      setIsEditingClosingDate(false);
    } catch (error: any) {
      console.error("Error saving closing date:", error);
      alert("Error al actualizar la fecha de cierre.");
    } finally {
      setIsSavingClosingDate(false);
    }
  };

  const processes = [
    {
      id: 'init_module',
      name: 'Inicializar Módulo',
      description: 'Genera el plan de cuentas base preparado y lo vincula con el ecosistema financiero actual. Borra la estructura anterior.',
      icon: Database,
      color: 'text-indigo-400',
      bgColor: 'bg-indigo-500/10',
      actionName: isInitialized ? 'Reinicializar Módulo' : 'Inicializar Módulo',
      requiresInit: false,
      handler: onInitialize
    },
    {
      id: 'init_balances',
      name: 'Generar Asiento de Apertura',
      description: 'Crea un asiento manual con los saldos actuales de tus cuentas bancarias y tarjetas, cuadrando con Capital Social.',
      icon: Calculator,
      color: 'text-emerald-400',
      bgColor: 'bg-emerald-500/10',
      actionName: 'Generar Asiento',
      requiresInit: true,
      handler: onGenerateInitialBalances
    },
    {
      id: 'settle_docs',
      name: 'Asentar Documentos de Gestión',
      description: 'Lee todas tus facturas de compra y venta pendientes y genera automáticamente los asientos contables de Operaciones.',
      icon: PlayCircle,
      color: 'text-amber-400',
      bgColor: 'bg-amber-500/10',
      actionName: 'Asentar Documentos',
      requiresInit: true,
      handler: onSettleDocuments
    }
  ];

  return (
    <div className="space-y-6">
      <div className="bg-zinc-900 border border-zinc-800 rounded-3xl p-6">
        <h2 className="text-xl font-bold text-white mb-2">Procesos Contables Automáticos</h2>
        <p className="text-zinc-400 text-sm">
          Ejecuta flujos preprogramados para configurar y mantener tu contabilidad al día. Puedes crear nuevos procesos parametrizados desde esta sección.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {processes.map(process => {
          const disabled = (process.requiresInit && !isInitialized) || runningProcess !== null || isInitializing;
          const isRunning = runningProcess === process.id || (process.id === 'init_module' && isInitializing);
          
          return (
            <div 
              key={process.id} 
              className={`bg-zinc-900 border transition-all rounded-3xl p-6 flex flex-col justify-between ${
                disabled ? 'border-zinc-800/50 opacity-60 grayscale' : 'border-zinc-800 hover:border-zinc-700'
              }`}
            >
              <div>
                <div className="flex items-center gap-4 mb-4">
                  <div className={`w-12 h-12 rounded-2xl flex items-center justify-center ${process.bgColor} ${process.color}`}>
                    {isRunning ? <RefreshCw className="animate-spin" size={24} /> : <process.icon size={24} />}
                  </div>
                  <div>
                    <h3 className="font-bold text-lg text-white">{process.name}</h3>
                    {process.requiresInit && isInitialized && (
                      <span className="text-[10px] uppercase font-bold tracking-widest text-emerald-500 flex items-center gap-1 mt-1">
                        <CheckCircle2 size={12} /> Habilitado
                      </span>
                    )}
                    {process.requiresInit && !isInitialized && (
                      <span className="text-[10px] uppercase font-bold tracking-widest text-amber-500 flex items-center gap-1 mt-1">
                        <AlertCircle size={12} /> Requiere Inicialización
                      </span>
                    )}
                  </div>
                </div>
                <p className="text-sm text-zinc-400 mb-6 min-h-[40px]">
                  {process.description}
                </p>
              </div>
              
              <button
                onClick={() => handleRunProcess(process.id, process.handler)}
                disabled={disabled}
                className={`w-full py-3 rounded-2xl font-bold flex items-center justify-center gap-2 transition-all ${
                  isRunning 
                    ? 'bg-zinc-800 text-zinc-400' 
                    : disabled 
                      ? 'bg-zinc-800 text-zinc-500 cursor-not-allowed'
                      : 'bg-zinc-800 hover:bg-zinc-700 text-white'
                }`}
              >
                {isRunning ? (
                  <>
                    <RefreshCw size={18} className="animate-spin" />
                    Procesando...
                  </>
                ) : (
                  <>
                    <PlayCircle size={18} />
                    {process.actionName}
                  </>
                )}
              </button>
            </div>
          );
        })}

        {/* Closing Date Card */}
        <div className="bg-zinc-900 border border-zinc-800 hover:border-zinc-700 transition-all rounded-3xl p-6 flex flex-col justify-between">
          <div>
            <div className="flex items-center gap-4 mb-4">
              <div className="w-12 h-12 rounded-2xl flex items-center justify-center bg-red-500/10 text-red-400">
                <Lock size={24} />
              </div>
              <div>
                <h3 className="font-bold text-lg text-white">Fecha de Cierre</h3>
                <span className="text-[10px] uppercase font-bold tracking-widest text-zinc-500 flex items-center gap-1 mt-1">
                  Bloqueo Contable y Financiero
                </span>
              </div>
            </div>
            <p className="text-sm text-zinc-400 mb-4 min-h-[40px]">
              Protege tus periodos contables ya declarados o conciliados, bloqueando cualquier modificación.
            </p>
            
            <div className="mb-6 p-4 bg-zinc-950 border border-zinc-800 rounded-2xl flex items-center justify-between">
              <div className="flex flex-col">
                <span className="text-[10px] text-zinc-500 uppercase tracking-widest font-bold mb-1">Cierre Actual</span>
                {!isEditingClosingDate ? (
                  <span className="text-white font-mono font-black text-sm">
                    {closingDate === '1900-01-01' ? 'Sin configurar' : closingDate}
                  </span>
                ) : (
                  <input
                    type="date"
                    value={newClosingDate}
                    onChange={(e) => setNewClosingDate(e.target.value)}
                    className="bg-zinc-800 border-none rounded md:w-36 text-white text-sm font-mono p-1 outline-none focus:ring-1 focus:ring-indigo-500/50"
                  />
                )}
              </div>
              <div className="text-zinc-600">
                <Calendar size={20} />
              </div>
            </div>
          </div>
          
          {!isEditingClosingDate ? (
            <button
              onClick={() => {
                setNewClosingDate(closingDate !== '1900-01-01' ? closingDate : new Date().toISOString().split('T')[0]);
                setIsEditingClosingDate(true);
              }}
              className="w-full py-3 rounded-2xl font-bold flex items-center justify-center gap-2 transition-all bg-zinc-800 hover:bg-zinc-700 text-white"
            >
              Modificar Cierre
            </button>
          ) : (
            <div className="flex gap-2">
              <button
                onClick={() => setIsEditingClosingDate(false)}
                className="flex-1 py-3 rounded-2xl font-bold flex items-center justify-center transition-all bg-zinc-800 hover:bg-zinc-700 text-zinc-400"
              >
                Cancelar
              </button>
              <button
                onClick={handleSaveClosingDate}
                disabled={isSavingClosingDate}
                className="flex-1 py-3 rounded-2xl font-bold flex items-center justify-center gap-2 transition-all bg-indigo-600 hover:bg-indigo-500 text-white disabled:opacity-50"
              >
                {isSavingClosingDate ? <RefreshCw className="animate-spin" size={18} /> : <Save size={18} />}
                Guardar
              </button>
            </div>
          )}
        </div>

      </div>
    </div>
  );
};
